var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
import { ENTER_KEY, ESCAPE_KEY } from '../../../const/keyNames';
import TabTrap from '../../../helpers/TabTrap';
import { BottomSheetContent, BottomSheetGrabber, BottomSheetWrapper, HeaderWrapper, Overlay, StyledFooter } from './subcomponents';
import modalKeyboardHandler from '../../../helpers/modalKeyboardHandler';
import useEventHandler from '../../customHooks/useEventHandler';
const Header = ({ header, touchStart, touchMove, touchEnd, closeModal }) => {
    const onKeyDownHandler = (event) => {
        if (event.key === ENTER_KEY || event.key === ESCAPE_KEY) {
            event.stopPropagation();
            closeModal();
        }
    };
    return (React.createElement(HeaderWrapper, { onTouchStart: touchStart, onTouchMove: touchMove, onTouchEnd: touchEnd },
        React.createElement(BottomSheetGrabber, { role: "button", "aria-label": "close", tabIndex: 0, onClick: () => closeModal(), onKeyDown: onKeyDownHandler }),
        header || null));
};
const SwipeableBottomSheet = (_a) => {
    var { id, children, header, className, footerButtons, isOpen, closeModal, swipeDownThreshold = 75 } = _a, props = __rest(_a, ["id", "children", "header", "className", "footerButtons", "isOpen", "closeModal", "swipeDownThreshold"]);
    // Indicates the initial position from where the modal starts its slide up transition
    const INITIAL_SLIDE_UP_POSITION = 800;
    const TRANSITION_DURATION = 400;
    const wrapperRef = useRef(null);
    const [postAnimationState, setPostAnimationState] = useState(false);
    const [startY, setStartY] = useState(0);
    const [translateY, setTranslateY] = useState(INITIAL_SLIDE_UP_POSITION);
    const [isDragging, setIsDragging] = useState(false);
    const resetModalStyles = () => {
        setTranslateY(INITIAL_SLIDE_UP_POSITION);
        closeModal();
    };
    useEventHandler('keydown', (event) => modalKeyboardHandler(event, { escapeHandler: resetModalStyles }));
    useEffect(() => {
        if (isOpen && wrapperRef.current) {
            document.body.style.overflow = 'hidden';
            wrapperRef.current.style.opacity = 1;
            setTranslateY(0);
            setPostAnimationState(true);
        }
        if (!isOpen && wrapperRef.current) {
            document.body.style.removeProperty('overflow');
            wrapperRef.current.style.opacity = 0;
            setTranslateY(INITIAL_SLIDE_UP_POSITION);
            setTimeout(() => {
                setPostAnimationState(false);
            }, TRANSITION_DURATION);
        }
        return () => {
            document.body.style.removeProperty('overflow');
        };
    }, [isOpen, id]);
    const handleTouchStart = (event) => {
        setIsDragging(true);
        setStartY(event.touches[0].clientY);
    };
    const handleTouchMove = (event) => {
        if (!isDragging)
            return;
        const deltaY = event.touches[0].clientY - startY;
        setTranslateY(deltaY);
    };
    const handleTouchEnd = () => {
        setIsDragging(false);
        /*
         * Swiped Down - translateY > 0
         * Swiped Up - translateY < 0
         */
        if (translateY > 0 && translateY > swipeDownThreshold) {
            resetModalStyles();
            return;
        }
        if (translateY > 0) {
            setTranslateY(translateY);
            return;
        }
        if (Math.abs(translateY) > swipeDownThreshold)
            setTranslateY(0);
    };
    return (React.createElement(Overlay, { isOpen: postAnimationState, onClick: (event) => {
            if (wrapperRef.current &&
                !wrapperRef.current.contains(event.target)) {
                resetModalStyles();
            }
        } }, (isOpen || postAnimationState) && (React.createElement(TabTrap, null,
        React.createElement(BottomSheetWrapper, Object.assign({ ref: wrapperRef, id: id, "aria-hidden": !isOpen, className: className, transitionDuration: TRANSITION_DURATION, style: {
                transform: `translate(-50%, ${translateY}px)`,
                opacity: 0
            } }, props),
            React.createElement(Header, { header: header, touchStart: handleTouchStart, touchMove: handleTouchMove, touchEnd: handleTouchEnd, closeModal: resetModalStyles }),
            React.createElement(BottomSheetContent, null, children),
            footerButtons && (React.createElement(StyledFooter, { vertical: "large" }, footerButtons)))))));
};
export default SwipeableBottomSheet;
